import React, { useState, useEffect } from 'react';
import { FaBell, FaUserCircle, FaSignOutAlt, FaBars } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useMobileMenu } from '../../overall_manager/contexts/MobileMenuContext';
import './Topbar.css';
import axios from 'axios';

const Topbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showDropdown, setShowDropdown] = useState(false);
    const userName = localStorage.getItem('userName') || 'Admin User';
    const { toggleMobileMenu } = useMobileMenu();
    const [userProfile, setUserProfile] = useState(null);
    const [animatedText, setAnimatedText] = useState("Welcome!");
    const baseUrl = "https://api.burrayhr.com";

    const handleLogout = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You will be logged out of the system",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#95a5a6',
            confirmButtonText: 'Yes, logout'
        }).then((result) => {
            if (result.isConfirmed) {
                localStorage.clear();
                navigate('/');
            }
        });
    };

    useEffect(() => {
        if (!userProfile) return;

        const getGreeting = () => {
            const hour = new Date().getHours();
            const name = userProfile?.employee_data?.full_name || 'Manager';
            if (hour < 12) return `Good Morning, ${name}`;
            if (hour < 18) return `Good Afternoon, ${name}`;
            return `Good Evening, ${name}`;
        };

        const companyName = userProfile?.company_data?.name || 'the company';

        const messages = [
            getGreeting(),
            `Welcome to ${companyName}!`,
            `Great leaders inspire greatness.`,
            `Empowering your team to succeed.`,
        ];

        let currentIndex = 0;
        setAnimatedText(messages[currentIndex]);

        const interval = setInterval(() => {
            currentIndex = (currentIndex + 1) % messages.length;
            setAnimatedText(messages[currentIndex]);
        }, 5000); // Change quote every 5 seconds

        return () => clearInterval(interval); // Cleanup on component unmount
    }, [userProfile]);

    useEffect(() => {
        fetchUserProfile();
    }, []);

    useEffect(() => {
        // Check first login and redirect if necessary
        if (userProfile?.user_data?.first_login) {
            const currentPath = location.pathname;
            if (currentPath !== '/hr-manager/change-password') {
                Swal.fire({
                    icon: 'warning',
                    title: 'Change Password Required',
                    text: 'You must change your password before accessing other pages.',
                    confirmButtonColor: '#1e3a8a',
                    allowOutsideClick: false,
                    allowEscapeKey: false
                }).then(() => {
                    navigate('/hr-manager/change-password');
                });
            }
        }
    }, [userProfile, location.pathname, navigate]);

    const fetchUserProfile = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/profile/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            console.log("user profile", response);
            setUserProfile(response.data);

            // Check first login immediately after fetching profile
            if (response.data?.user_data?.first_login) {
                const currentPath = location.pathname;
                if (currentPath !== '/hr-manager/change-password') {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Change Password Required',
                        text: 'You must change your password before accessing other pages.',
                        confirmButtonColor: '#1e3a8a',
                        allowOutsideClick: false,
                        allowEscapeKey: false
                    }).then(() => {
                        navigate('/hr-manager/change-password');
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
            if (error.response?.status === 401) {
                localStorage.clear();
                navigate('/login');
            }
        }
    };

    // Prevent dropdown menu from showing if it's first login
    const handleDropdownClick = () => {
        if (userProfile?.user_data?.first_login) {
            Swal.fire({
                icon: 'warning',
                title: 'Change Password Required',
                text: 'You must change your password before accessing other features.',
                confirmButtonColor: '#1e3a8a'
            });
            return;
        }
        setShowDropdown(!showDropdown);
    };

    return (
        <div className="topbar">
            <div className="topbar-left">
                {/* Only show menu button if not first login */}
                {!userProfile?.user_data?.first_login && (
                    <button className="menu-btn" onClick={toggleMobileMenu} aria-label="Toggle Menu">
                        <FaBars style={{fontSize: '2rem'}} className="menu-icon" />
                    </button>
                )}
                <h4 key={animatedText} className="animated-greeting" style={{
                    marginLeft: userProfile?.user_data?.first_login ? '20px' : '1rem'
                }}>
                    {animatedText}
                </h4>
            </div>
            
            <div className="topbar-right">
                <div className="user-profile" onClick={handleDropdownClick}>
                    <img 
                        style={{
                            width: '50px', 
                            height: '50px', 
                            backgroundColor: 'white', 
                            borderRadius: '50%'
                        }} 
                        src={userProfile?.employee_data?.photo} 
                        alt="Profile" 
                        className="avatar" 
                    />
                    <span className="username">{userProfile?.employee_data?.full_name}</span>
                    
                    {showDropdown && !userProfile?.user_data?.first_login && (
                        <div className="dropdown-menu">
                            <div className="dropdown-header">
                                <img 
                                    style={{
                                        width: '50px', 
                                        height: '50px', 
                                        backgroundColor: 'white', 
                                        borderRadius: '10%'
                                    }} 
                                    src={userProfile?.employee_data?.photo} 
                                    alt="Profile" 
                                    className="dropdown-avatar" 
                                />
                                <div className="user-info">
                                    <span className="user-name">{userProfile?.employee_data?.full_name}</span>
                                    <span className="user-role">{userProfile?.company_data?.name}</span>
                                </div>
                            </div>
                            <div className="dropdown-divider"></div>
                            <button className="dropdown-item" onClick={handleLogout}>
                                <FaSignOutAlt />
                                <span>Logout</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <style jsx global>{`
                .animated-greeting {
                    animation: fadeIn 0.8s ease-in-out;
                    font-size: 1.1rem;
                    font-weight: 400;
                    color: #fff;
                    white-space: nowrap;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default Topbar;
